// src/components/CollectionEnrollmentPage.js
import React, { useMemo, useState, useEffect, useContext } from "react";
import {
  Box,
  Grid,
  Stack,
  Typography,
  Chip,
  Avatar,
  LinearProgress,
  Card,
  CardContent,
  Divider,
  IconButton,
  Tooltip,
  TextField,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
} from "@mui/material";
import { motion } from "framer-motion";
import { useParams, useLocation } from "react-router-dom";

import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import StarRateIcon from "@mui/icons-material/StarRate";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import LocalMallIcon from "@mui/icons-material/LocalMall";
import MilitaryTechIcon from "@mui/icons-material/MilitaryTech";
import VideogameAssetIcon from "@mui/icons-material/VideogameAsset";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
// Platform components
import NeoButton from "./NeoButton";
import CharacterCard from "./CharacterCard";
import SwipeInspectModal from "./SwipeInspectModal";
import { MainContext } from "../App";

// 🔥 Firestore
import {
  getFirestore,
  doc,
  onSnapshot,
  getDoc,
  collection,
  getDocs,
  query,
  where,
  limit,
  orderBy,
  updateDoc,
} from "firebase/firestore";
import { useAuthStore } from "../@shared/store/useAuthStore";
import { Visibility } from "@mui/icons-material";
import { dialogTitleSx } from "../@shared/ui/tokens";

const DISCORD_URL = "https://discord.gg/u3qapXHRea";

/* ------------------ CONFIG (Milestones) ------------------ */
const MILESTONES = [
  { tier: "Bronze", points: 500, reward: "+1 IP Slot" },
  { tier: "Silver", points: 1500, reward: "+1 IP Slot" },
  { tier: "Gold", points: 3000, reward: "+2 IP Slots" },
  {
    tier: "Platinum",
    points: 7500,
    reward: "+3 IP Slots + Season Promo Feature",
  },
  {
    tier: "Diamond",
    points: 10000,
    reward: "+5 IP Slots + Next Season Guaranteed + Lore Integration",
  },
];

/* ------------------ POINTS (breakdown UI data) ------------------ */
const POINT_RULES = [
  {
    label: "Referred account created",
    points: 50,
    icon: <PersonAddAltIcon fontSize="small" style={{ color: "#ffd24d" }} />,
  },
  {
    label: "Match played",
    points: 25,
    icon: <VideogameAssetIcon fontSize="small" style={{ color: "#ffd24d" }} />,
  },
  {
    label: "Match won",
    points: 50,
    icon: <EmojiEventsIcon fontSize="small" style={{ color: "#ffd24d" }} />,
  },
  {
    label: "Pack purchase",
    points: 100,
    icon: <LocalMallIcon fontSize="small" style={{ color: "#ffd24d" }} />,
  },
  {
    label: "Season pass purchase",
    points: 200,
    icon: <MilitaryTechIcon fontSize="small" style={{ color: "#ffd24d" }} />,
  },
];

/* ------------------ helpers ------------------ */
const formatNumber = (n) => new Intl.NumberFormat().format(n || 0);
const useIsNarrow = () => useMediaQuery("(max-width:900px)");
const normalizeCollectionName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ");

function computeProgress(points) {
  const idx = MILESTONES.findIndex((m) => points < m.points);
  if (idx === -1)
    return {
      currentTierIndex: MILESTONES.length - 1,
      nextTarget: null,
      pctToNext: 100,
    };
  const prevPoints = idx === 0 ? 0 : MILESTONES[idx - 1].points;
  const span = MILESTONES[idx].points - prevPoints;
  const into = Math.max(0, points - prevPoints);
  const pct = Math.max(0, Math.min(100, Math.round((into / span) * 100)));
  return {
    currentTierIndex: Math.max(0, idx - 1),
    nextTarget: MILESTONES[idx].points,
    pctToNext: pct,
  };
}

function deriveSlots(points) {
  let slots = 0;
  for (const m of MILESTONES) {
    if (points >= m.points) {
      const match = m.reward.match(/\+(\d+) IP Slot/);
      if (match) slots += Number(match[1]);
    }
  }
  return slots;
}

/* ------------------ **PLATFORM SKIN** shared styles ------------------ */
const useFrameColors = (Theme) => ({
  outerGlow:
    "inset 0 0 0 2px #231b7a, inset 0 0 0 6px #0a5cff33, 0 8px 30px rgba(10,92,255,.25)",
  border: `2px solid ${Theme.palette.tirciary.light}`,
  capGrad: "linear-gradient(180deg, #3b1f8a 0%, #341a7c 100%)",
  bodyGrad: "linear-gradient(180deg, #2a184f 0%, #231543 100%)",
});

const Stud = ({ sx }) => (
  <Box
    sx={{
      position: "absolute",
      width: 10,
      height: 10,
      borderRadius: "50%",
      background:
        "radial-gradient(ellipse at 30% 30%, #ffd24d 0%, #ffb400 35%, #7a4e00 100%)",
      boxShadow: "0 0 6px rgba(255,200,50,.6)",
      border: "3px solid #000",
      ...sx,
    }}
  />
);

function BlockingApprovalModal({ open, isOwner, discordUrl = DISCORD_URL }) {
  // Prevent ALL close attempts (escape/backdrop)
  const handleClose = (_event, _reason) => {
    // Intentionally do nothing to keep the modal open
    return;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown
      // extra safety: MUI still passes "backdropClick" reason—ignored above
      PaperProps={{
        sx: {
          width: "90%",
          maxWidth: 560,
          borderRadius: 3,
          overflow: "hidden",
          bgcolor: "#231543",
          border: (t) => `2px solid ${t.palette.tirciary?.light || "#3a1fa4"}`,
          boxShadow:
            "inset 0 0 0 2px #231b7a, inset 0 0 0 6px #0a5cff33, 0 8px 30px rgba(10,92,255,.25)",
        },
      }}
      BackdropProps={{
        sx: { backgroundColor: "rgba(0,0,0,0.75)" },
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          ...dialogTitleSx,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <InfoOutlinedIcon sx={{ color: "#ffd24d" }} />
          <DialogTitle
            sx={{
              p: 0,
              m: 0,
              fontWeight: 900,
              color: "#ffd24d",
              textShadow:
                "0 2px 0 #5c2a00, -1.5px -1.5px 0 #000, 1.5px -1.5px 0 #000, -1.5px 1.5px 0 #000, 1.5px 1.5px 0 #000",
            }}
          >
            Approval Required
          </DialogTitle>
        </Stack>
      </Box>

      <DialogContent sx={{ pt: 2.5, pb: 3 }}>
        {isOwner ? (
          <Stack spacing={2}>
            <Typography variant="body1" sx={{ color: "#c9b7ff" }}>
              Your collection hasn’t been approved yet. To proceed, join our
              Discord and open a support ticket so the team can review and
              approve your enrollment.
            </Typography>

            <NeoButton
              text="Open a ticket on Discord"
              endAdorn={<OpenInNewIcon sx={{ ml: 1 }} />}
              color="#3478F6"
              width={100}
              height="44px"
              onClick={() => window.open(discordUrl, "_blank")}
            />

            <Typography
              variant="caption"
              sx={{ opacity: 0.85, color: "#c9b7ff" }}
            >
              After approval, refresh this page to access all enrollment
              features.
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={2}>
            <Typography variant="body1" sx={{ color: "#c9b7ff" }}>
              This collection has not yet been approved for enrollment. Please
              check back later.
            </Typography>
            <Typography
              variant="caption"
              sx={{ opacity: 0.85, color: "#c9b7ff" }}
            >
              Approval generally occurs after the creator opens a Discord
              ticket.
            </Typography>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

const FrameCard = ({ title, icon, right, children }) => {
  const { Theme } = useContext(MainContext);
  const C = useFrameColors(Theme);
  return (
    <Card
      sx={{
        borderRadius: 2,
        background: C.bodyGrad,
        boxShadow: C.outerGlow,
        position: "relative",
      }}
    >
      <Box
        sx={{
          p: 1.25,
          ...dialogTitleSx,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          {icon}
          <Typography
            sx={{
              fontWeight: 900,
              color: "#ffd24d",
              textShadow:
                "0 2px 0 #5c2a00, -1.5px -1.5px 0 #000, 1.5px -1.5px 0 #000, -1.5px 1.5px 0 #000, 1.5px 1.5px 0 #000",
            }}
          >
            {title}
          </Typography>
        </Stack>
        {right}
      </Box>
      <CardContent sx={{ borderTop: C.border }}>{children}</CardContent>
      <Stud sx={{ left: 12, bottom: 12 }} />
      <Stud sx={{ right: 12, bottom: 12 }} />
    </Card>
  );
};

/* ------------------ Firestore: resolve doc id from slug, then SUBSCRIBE live ------------------ */
function useCollectionFromUrlLive(slugOrId) {
  const [state, setState] = useState({
    loading: true,
    data: null,
    error: null,
  });

  useEffect(() => {
    let alive = true;
    let unsubDoc = null;
    let unsubCards = null;

    (async () => {
      try {
        const db = getFirestore();
        let colId = null;

        const tryDoc = await getDoc(
          doc(db, "collections_onboarding", slugOrId)
        );
        if (tryDoc.exists()) {
          colId = tryDoc.id;
        } else {
          const qBySlug = query(
            collection(db, "collections_onboarding"),
            where("slug", "==", slugOrId),
            limit(1)
          );
          const qs = await getDocs(qBySlug);
          if (!qs.empty) colId = qs.docs[0].id;
        }

        if (!colId) {
          if (!alive) return;
          setState({ loading: false, data: null, error: "not_found" });
          return;
        }

        unsubDoc = onSnapshot(
          doc(db, "collections_onboarding", colId),
          (snap) => {
            if (!snap.exists()) {
              if (!alive) return;
              setState({ loading: false, data: null, error: "not_found" });
              return;
            }
            const d = snap.data();

            const supportersCount = Array.isArray(d.supporters)
              ? d.supporters.length
              : Number(d.supporters || 0);

            const base = {
              id: snap.id,
              slug: d.slug || slugOrId,
              name: d.name || "Unnamed Collection",
              logoUrl: d.logoUrl || "",
              bannerUrl: d.bannerUrl || "",
              discord: d.discord || null,
              mintPage: d.mintPage || null,
              totalPoints: Number(d.totalPoints || 0),
              supporters: supportersCount,
              rank: d.rank || null,
              currentCards: [],
              approved: d.approved
            };

            if (alive)
              setState((prev) => ({
                loading: false,
                error: null,
                data: { ...(prev?.data || base), ...base },
              }));
          }
        );

        const cardsColRef = collection(
          db,
          "collections_onboarding",
          colId,
          "current_cards"
        );
        unsubCards = onSnapshot(cardsColRef, (qs) => {
          const currentCards = qs.docs.map((d) => ({ id: d.id, ...d.data() }));
          if (alive)
            setState((prev) => ({
              loading: false,
              error: null,
              data: prev?.data
                ? { ...prev.data, currentCards }
                : { currentCards },
            }));
        });
      } catch (e) {
        if (!alive) return;
        setState({ loading: false, data: null, error: e?.message || "error" });
      }
    })();

    return () => {
      alive = false;
      if (unsubDoc) unsubDoc();
      if (unsubCards) unsubCards();
    };
  }, [slugOrId]);

  return state;
}

/* ------------------ Firestore: LIVE leaderboard of enrolling collections ------------------ */
// 🔥 Firestore: LIVE leaderboard of enrolling collections ------------------
export function useEnrollmentLeaderboardLive({ limitCount = 20 } = {}) {
  const [state, setState] = useState({ loading: true, items: [] });

  useEffect(() => {
    const db = getFirestore();
    let qRef = query(
      collection(db, "collections_onboarding"),
      where("isEnrolling", "==", true),
      orderBy("totalPoints", "desc"),
      limit(limitCount)
    );

    let unsub = onSnapshot(
      qRef,
      (qs) => {
        const items = qs.docs.map((d) => {
          const data = d.data();
          const supportersCount = Array.isArray(data.supporters)
            ? data.supporters.length
            : Number(data.supporters || 0);

          return {
            id: d.id,
            name: data.name || "Unnamed Collection",
            logoUrl: data.logoUrl || "",
            bannerUrl: data.bannerUrl || "",
            points: Number(data.totalPoints || 0),
            totalPoints: Number(data.totalPoints || 0),
            supporters: supportersCount,
            discord: data.discord || null,
            mintPage: data.mintPage || null,
            slug: data.slug || d.id,
          };
        });
        setState({ loading: false, items });
      },
      () => {
        // Fallback: no `isEnrolling` index/field
        const qAll = query(
          collection(getFirestore(), "collections_onboarding"),
          orderBy("totalPoints", "desc"),
          limit(limitCount)
        );
        const unsubAll = onSnapshot(qAll, (qs2) => {
          const items = qs2.docs.map((d) => {
            const data = d.data();
            const supportersCount = Array.isArray(data.supporters)
              ? data.supporters.length
              : Number(data.supporters || 0);

            return {
              id: d.id,
              name: data.name || "Unnamed Collection",
              logoUrl: data.logoUrl || "",
              bannerUrl: data.bannerUrl || "",
              points: Number(data.totalPoints || 0),
              totalPoints: Number(data.totalPoints || 0),
              supporters: supportersCount,
              discord: data.discord || null,
              mintPage: data.mintPage || null,
              slug: data.slug || d.id,
            };
          });
          setState({ loading: false, items });
        });
        unsub = () => unsubAll();
      }
    );

    return () => unsub && unsub();
  }, [limitCount]);

  return state;
}


/* ------------------ small components ------------------ */
const ProgressBlock = ({ points }) => {
  const { Theme } = useContext(MainContext);
  const { currentTierIndex, nextTarget, pctToNext } = useMemo(
    () => computeProgress(points),
    [points]
  );
  const currentTier = MILESTONES[Math.max(0, currentTierIndex)]?.tier || "None";
  const nextTier = nextTarget
    ? MILESTONES[currentTierIndex + 1]?.tier
    : "Maxed";

  return (
    <FrameCard
      title="Progress"
      icon={<TrendingUpIcon style={{ color: "#ff7ce5" }} />}
      right={
        <Chip
          icon={<StarRateIcon />}
          label={`${formatNumber(points)} CP`}
          color="primary"
        />
      }
    >
      <Box>
        <Stack direction="row" spacing={1} alignItems="center" mb={1}>
          <Chip
            label={`Current: ${currentTier}`}
            size="small"
            color="secondary"
          />
          <Typography variant="body2" sx={{ opacity: 0.8, color: "#c9b7ff" }}>
            {nextTier !== "Maxed"
              ? `Next: ${nextTier} at ${formatNumber(nextTarget)} CP`
              : "Top tier reached"}
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={pctToNext}
          sx={{
            height: 10,
            borderRadius: 999,
            bgcolor: "#2b1d63",
            ["& .MuiLinearProgress-bar"]: { backgroundColor: "#b90c98" },
          }}
        />
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 0.75, opacity: 0.8, color: "#c9b7ff" }}
        >
          {pctToNext}% to next unlock
        </Typography>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={1}>
          {MILESTONES.map((m, i) => {
            const achieved = points >= m.points;
            return (
              <Grid key={m.tier} item xs={12} sm={6} md={4} lg={3}>
                <Stack
                  component={motion.div}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{
                    p: 1,
                    borderRadius: 1.5,
                    border: (theme) =>
                      `1px solid ${theme.palette.tirciary.light}`,
                    background: achieved ? "#2b1d63" : "#231543",
                  }}
                >
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      bgcolor: achieved ? "secondary.main" : "secondary.light",
                    }}
                  >
                    <StarRateIcon fontSize="small" />
                  </Avatar>
                  <Box>
                    <Typography variant="body2" fontWeight={700} color="#fff">
                      {m.tier}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ opacity: 0.8, color: "#c9b7ff" }}
                    >
                      {formatNumber(m.points)} CP • {m.reward}
                    </Typography>
                  </Box>
                </Stack>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </FrameCard>
  );
};

const PointsBreakdown = () => (
  <FrameCard
    title="Points Breakdown"
    icon={<InfoOutlinedIcon style={{ color: "#ff7ce5" }} />}
  >
    <Grid container spacing={1}>
      {POINT_RULES.map((r) => (
        <Grid item xs={12} sm={6} key={r.label}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              p: 1,
              borderRadius: 1.5,
              background: "#231543",
              border: "1px solid #3a1fa4",
            }}
          >
            {r.icon}
            <Typography variant="body2" sx={{ flex: 1, color: "#fff" }}>
              {r.label}
            </Typography>
            <Chip
              size="small"
              label={`+${r.points} CP`}
              sx={{ bgcolor: "#b90c98", color: "#fff" }}
            />
          </Stack>
        </Grid>
      ))}
    </Grid>
  </FrameCard>
);

const ReferralPanel = ({ collection }) => {
  const { Theme } = useContext(MainContext);
  const origin =
    typeof window !== "undefined"
      ? window.location.origin
      : "https://infinftroyale.com";
  const referralUrl = `${origin}/referral/?ref=col-${collection.id}`;
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(referralUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch {
      setCopied(false);
    }
  };

  return (
    <FrameCard
      title="Support This Collection"
      icon={<GroupAddIcon style={{ color: "#ff7ce5" }} />}
      right={
        <Tooltip title={copied ? "Copied!" : "Copy link"}>
          <IconButton onClick={copy} sx={{ color: "#fff" }}>
            <ContentCopyIcon />
          </IconButton>
        </Tooltip>
      }
    >
      <Stack
        direction={{ xs: "column" }}
        spacing={2}
        alignItems={{ xs: "stretch", md: "center" }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="h6"
            fontWeight={900}
            sx={{
              color: Theme.palette.text.main,
              textShadow: "2px 3px 6px #000",
            }}
          >
            Create an account from this page to start earning CP for{" "}
            {collection.name}.
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center" mt={1.5}>
            <TextField
              size="small"
              fullWidth
              value={referralUrl}
              InputProps={{ readOnly: true  }}
              style={{
                color:'white',
              }}
            />
          </Stack>
        </Box>
        <Stack minWidth={220} spacing={1}>
          <NeoButton
            text="Support & Sign Up"
            startAdorn={<GroupAddIcon sx={{ mr: 1 }} />}
            color={Theme.palette.primary.main}
            width={100}
            height="44px"
            onClick={() => (window.location.href = referralUrl)}
          />
          <Typography
            variant="caption"
            sx={{ opacity: 0.85, color: "#c9b7ff" }}
          >
            Creating a referred account: <b>+50 CP</b>. Matches, wins, packs &
            season passes earn even more.
          </Typography>
        </Stack>
      </Stack>
    </FrameCard>
  );
};

const SocialLinks = ({ discord, mintPage }) => (
  <Stack direction="row" spacing={1}>
    {discord && (
      <NeoButton
        text="Discord"
        endAdorn={<OpenInNewIcon sx={{ ml: 1 }} />}
        color="#3478F6"
        width={100}
        height="36px"
        onClick={() => window.open(discord, "_blank")}
      />
    )}
    {/* {mintPage && (
      <NeoButton
        text="NFT Page"
        endAdorn={<OpenInNewIcon sx={{ ml: 1 }} />}
        color="#3478F6"
        width={100}
        height="36px"
        onClick={() => window.open(mintPage, "_blank")}
      />
    )} */}
  </Stack>
);

const MiniLeaderboard = ({ items, loading }) => {
  const { navigate } = useContext(MainContext);

  return (
    <FrameCard
      title="Season Enrollment Leaderboard"
      icon={<WorkspacePremiumIcon style={{ color: "#ff7ce5" }} />}
    >
      {loading ? (
        <Stack spacing={1}>
          <LinearProgress />
          <LinearProgress />
        </Stack>
      ) : (
        <Stack spacing={1}>
          {items.map((row, idx) => (
            <Stack
              key={row.id}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              onClick={() => navigate(`/enrollment/${row.slug || row.id}`)}
              sx={{
                p: 1,
                borderRadius: 1.5,
                bgcolor: idx === 0 ? "#2b1d63" : "#231543",
                border: idx === 0 ? "1px solid #ff7ce5" : "1px solid #3a1fa4",
                cursor: "pointer",
                transition: "transform 120ms ease, background-color 120ms ease",
                "&:hover": {
                  transform: "translateY(-1px)",
                  bgcolor: idx === 0 ? "#34207a" : "#2b1d63",
                },
              }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Avatar
                  src={row.logoUrl}
                  alt={row.name}
                  sx={{ width: 28, height: 28 }}
                />
                <Typography variant="body2" fontWeight={700} color="#fff">
                  {idx + 1}. {row.name}
                </Typography>
              </Stack>
              <Chip
                size="small"
                label={`${formatNumber(row.points)} CP`}
                color={idx === 0 ? "secondary" : "primary"}
              />
            </Stack>
          ))}
        </Stack>
      )}
    </FrameCard>
  );
};

/* ------------------ header & cards ------------------ */
export const HeaderCard = ({ collection }) => {
  const { Theme, navigate } = useContext(MainContext);
  const C = useFrameColors(Theme);
  const {
    name,
    bannerUrl,
    logoUrl,
    supporters,
    totalPoints,
    discord,
    mintPage,
    slug,
  } = collection;

  const slots = deriveSlots(totalPoints);

  const location = useLocation();

  // detail page: /enrollment/:slug (e.g. /enrollment/poot)
  const pathParts = location.pathname.split("/").filter(Boolean);
  const isEnrollmentDetail =
    pathParts[0] === "enrollment" && pathParts.length === 2;

  const isPreviewCard = !isEnrollmentDetail;

  const handleView = () => {
    navigate(`/enrollment/${slug}`);
  };

  const handleSupport = () => {
    const referralUrl = `/referral/?ref=col-${collection.id}`
   
      navigate(`${referralUrl}`);
    
  };

  const handleOpenNftPage = () => {
    if (mintPage) {
      window.open(mintPage, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Card
      sx={{
        borderRadius: 2,
        overflow: "hidden",
        background: C.bodyGrad,
        boxShadow: C.outerGlow,
        position: "relative",
      }}
    >
      {/* FLOATING ACTION BUTTONS FOR PREVIEW CONTEXTS */}
      {isPreviewCard && (
        <>
          <Box
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              zIndex: 3,
              width:'50%'
            }}
          >
            <NeoButton
              isIcon={true}
              icon={<Visibility sx={{color:'white'}}/>}
              color={Theme.palette.primary.main}
              width={32}
              height={32}
              onClick={handleView}
            />
          </Box>

          <Box
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              zIndex: 3,
              width:'50%',
              display:'flex',
              justifyContent:'flex-end'
            }}
          >
            <NeoButton
              text="Support"
              color={Theme.palette.secondary.main}
              width={70}
              height={32}
              onClick={handleSupport}
            />
          </Box>
        </>
      )}

      {/* BANNER */}
      <Box
        sx={{
          position: "relative",
          height: 220,
          width: "100%",
          backgroundImage: `url(${bannerUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.6) 100%)",
          }}
        />
      </Box>

      {/* CONTENT */}
      <Box sx={{ p: 2, borderTop: C.border }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ xs: "flex-start", sm: "flex-end" }}
          spacing={2}
        >
          {/* AVATAR */}
          <Box
            sx={{
              position: "relative",
              display: "inline-flex",
              mt: -6,
              ml: 2,
              borderRadius: "50%",
              border: `4px solid ${Theme.palette.tirciary.dark}`,
              width: 96,
              height: 96,
              overflow: "hidden",
              boxShadow: C.outerGlow,
            }}
          >
            <Avatar src={logoUrl} alt={name} sx={{ width: 96, height: 96 }} />
          </Box>

          {/* TEXT + META */}
          <Stack spacing={0.5} sx={{ mt: { xs: 1, sm: 0 }, flex: 1 }}>
            <Typography variant="h6" fontWeight={900} color="#fff">
              {name}
            </Typography>

            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1}
              alignItems={{ xs: "flex-start", sm: "center" }}
            >
              <Typography
                variant="body2"
                sx={{ opacity: 0.85, color: "#c9b7ff" }}
              >
                @{slug}
              </Typography>
              <SocialLinks discord={discord} mintPage={mintPage} />
            </Stack>

            {/* CHIPS + (optional) NFT PAGE BUTTON */}
            <Stack
              direction="row"
              spacing={1}
              mt={1}
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1}>
                <Chip
                  icon={<GroupAddIcon />}
                  label={`${formatNumber(supporters)} supporters`}
                  color="secondary"
                />
                <Chip
                  icon={<SportsEsportsIcon />}
                  label={`${formatNumber(slots)} IP slots`}
                  color="primary"
                />
              </Stack>

              {/* NFT PAGE BUTTON ONLY ON DETAIL PAGE */}
              {!isPreviewCard && mintPage && (
                <NeoButton
                  text="NFT Page"
                  color={Theme.palette.primary.main}
                  endAdorn={<OpenInNewIcon sx={{ ml: 1 }} />}
                  width={40}
                  height="32px"
                  onClick={handleOpenNftPage}
                />
              )}
            </Stack>
          </Stack>
        </Stack>
      </Box>

      <Stud sx={{ left: 12, bottom: 12 }} />
      <Stud sx={{ right: 12, bottom: 12 }} />
    </Card>
  );
};



const CardSlot = ({ empty, card, onClick }) => (
  <Box
    sx={{
      border: (theme) => `1px dashed ${theme.palette.tirciary.light}`,
      borderRadius: 2,
      p: 1,
      height: 240,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      bgcolor: empty ? "#231543" : "#2a184f",
      cursor: empty ? "default" : "pointer",
    }}
    onClick={empty ? undefined : onClick}
  >
    {empty ? (
      <Stack alignItems="center" spacing={0.5}>
        <Typography variant="caption" sx={{ opacity: 0.85, color: "#c9b7ff" }}>
          Empty Slot
        </Typography>
        <Typography variant="caption" sx={{ opacity: 0.7, color: "#c9b7ff" }}>
          Unlocked via milestones
        </Typography>
      </Stack>
    ) : (
      <Box sx={{ width: "100%", height: "100%" }}>
        <CharacterCard
          {...card}
          containerHeight={240}
          heightPer={100}
          shine={false}
        />
      </Box>
    )}
  </Box>
);

const CurrentCardsGrid = ({ collection: collectionData }) => {
  const slots = deriveSlots(collectionData.totalPoints);
  const [matchingCards, setMatchingCards] = useState([]);
  const [inspectedIndex, setInspectedIndex] = useState(null);

  useEffect(() => {
    if (!collectionData?.name) {
      setMatchingCards([]);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const snapshot = await getDocs(collection(getFirestore(), "Character cards"));
        if (cancelled) return;

        const targetName = normalizeCollectionName(collectionData.name);
        const cards = snapshot.docs
          .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
          .filter(
            (card) =>
              normalizeCollectionName(card?.collectionName) === targetName,
          );

        setMatchingCards(cards);
      } catch (e) {
        if (!cancelled) {
          console.error("Failed to load collection character cards:", e);
          setMatchingCards([]);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [collectionData?.name]);

  const currentCards = collectionData.currentCards || [];
  const currentCardIds = new Set(currentCards.map((card) => card?.id).filter(Boolean));
  const fallbackCards = matchingCards.filter((card) => !currentCardIds.has(card.id));
  const empties = Math.max(0, slots - currentCards.length);
  const fillCards = fallbackCards.slice(0, empties);
  const remainingEmptySlots = Math.max(0, empties - fillCards.length);
  const inspectableCards = [...currentCards, ...fillCards].map((card) => ({
    ...card,
    type: card?.type || "Character",
  }));
  const items = [
    ...inspectableCards,
    ...Array.from({ length: remainingEmptySlots }, (_, i) => ({
      __empty: true,
      id: `empty-${i}`,
    })),
  ];

  return (
    <FrameCard
      title="Current Cards In-Game"
      icon={<InfoOutlinedIcon style={{ color: "#ff7ce5" }} />}
    >
      <Grid container spacing={1.5}>
        {items.length === 0 && (
          <Grid item xs={12}>
            <Typography
              variant="body2"
              sx={{ opacity: 0.85, color: "#c9b7ff" }}
            >
              No slots unlocked yet. Earn CP to unlock your first IP slot.
            </Typography>
          </Grid>
        )}
        {items.map((it, index) => (
          <Grid key={it.id} item xs={6} sm={4} md={3} lg={3}>
            {it.__empty ? (
              <CardSlot empty />
            ) : (
              <CardSlot card={it} onClick={() => setInspectedIndex(index)} />
            )}
          </Grid>
        ))}
      </Grid>
      <SwipeInspectModal
        open={inspectedIndex !== null}
        onClose={() => setInspectedIndex(null)}
        cards={inspectableCards}
        startIndex={inspectedIndex ?? 0}
      />
    </FrameCard>
  );
};

/* ------------------ main page ------------------ */
export default function CollectionEnrollmentPage() {
  const { Theme } = useContext(MainContext);
  const { userData } = useAuthStore();
  const slugOrId = useParams(); // route: /enrollment/:enrollmentId
  const {
    loading,
    data: collection,
    error,
  } = useCollectionFromUrlLive(slugOrId?.enrollmentId);
  const { loading: lbLoading, items: leaderboardItems } =
    useEnrollmentLeaderboardLive({ limitCount: 20 });

  const isOwner =
    userData?.id && collection?.id && collection.id === userData?.id;
  const isAdmin = !!userData?.roles?.includes("admin");

  useEffect(() => {
    if (!collection?.id) return;
    if (!isOwner && !isAdmin) return;

    const expectedSlots = deriveSlots(Number(collection.totalPoints || 0));
    const currentSlots = Number(collection.unlockedSlots || 0);

    if (expectedSlots === currentSlots) return;

    let cancelled = false;

    (async () => {
      try {
        await updateDoc(doc(getFirestore(), "collections_onboarding", collection.id), {
          unlockedSlots: expectedSlots,
        });
      } catch (e) {
        if (!cancelled) {
          console.error("Failed to sync unlockedSlots from totalPoints:", e);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    collection?.id,
    collection?.totalPoints,
    collection?.unlockedSlots,
    isOwner,
    isAdmin,
  ]);

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
      </Box>
    );
  }

  if (error === "not_found" || !collection) {
    return (
      <Box sx={{ p: 3, maxWidth: 900, mx: "auto" }}>
        <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
          Collection not found
        </Typography>
        <Typography sx={{ opacity: 0.85, color: "#c9b7ff" }}>
          We couldn’t find an enrollment page for “{slugOrId?.enrollmentId}”.
          Double-check the link or ask the creator for the correct URL.
        </Typography>
      </Box>
    );
  }

  const { totalPoints } = collection;
  const isApproved = collection.approved;

  return (
    <Grid
      container
      item
      xs={12}
      sx={{ justifyContent: "center", background: "#c9b7ff" }}
    >
      <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: "auto" }}>
        {/* HEADER */}
        <HeaderCard collection={collection} />

        {/* CONTENT GRID */}
        <Grid container spacing={3} sx={{ mt: 0 }}>
          <Grid item xs={12} md={8}>
            <Stack spacing={2.5}>
              <ReferralPanel collection={collection} />
              <CurrentCardsGrid collection={collection} />
              <ProgressBlock points={totalPoints} />
              <PointsBreakdown />
            </Stack>
          </Grid>

          <Grid item xs={12} md={4}>
            <MiniLeaderboard items={leaderboardItems} loading={lbLoading} />
            <FrameCard
              title="How It Works"
              icon={<InfoOutlinedIcon style={{ color: "#ff7ce5" }} />}
            >
              <Typography
                variant="body2"
                sx={{ mt: 1, opacity: 0.9, color: "#c9b7ff" }}
              >
                Create an account from this page to link your profile to this
                collection. Playing matches, buying packs, and purchasing a
                season pass earn Community Points (CP) that unlock IP Slots
                before the next season.
              </Typography>
            </FrameCard>
          </Grid>
        </Grid>

        {/* FOOTER NOTE */}
        <Box mt={4}>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            Open Enrollment runs for a limited time each season. Progress resets
            every season.
          </Typography>
        </Box>
      </Box>

      <BlockingApprovalModal
        open={!isApproved} // const isApproved = collection.approved === true;
        isOwner={isOwner} // const isOwner = userData?.id && collection.ownerId && collection.ownerId === userData?.id;
      />
    </Grid>
  );
}
